/**
 * 화면의 본동사 버튼은 제목 줄에 둔다. 상단 바 글자로 다시 쓰지 않는다.
 * 상단 「만들기」는 플랜이 열려 있을 때 Task, 플랜, 팀, 사용자 초대를 연다.
 * 워크스페이스 탭에서는 숨긴다.
 * `undefined`와 `null`은 이 화면의 본동사가 없다는 뜻이다.
 */
import type { FluentIcon } from "@fluentui/react-icons";
import { useLayoutEffect, useRef } from "react";
import { create } from "zustand";

export type PageAction = {
  label: string;
  icon?: FluentIcon;
  onClick: () => void;
  disabled?: boolean;
  /** 본동사는 제목 줄에 있다. 상단 바 버튼 글자는 항상 「만들기」다. */
  inHeader?: boolean;
};

type Slot = {
  id: number;
  action: PageAction | null;
};

type PageActionState = {
  action: PageAction | null | undefined;
};

let slots: Slot[] = [];
let nextSlotId = 1;

export const usePageActionStore = create<PageActionState>(() => ({
  action: undefined,
}));

function visibleAction(): PageAction | null | undefined {
  return slots.length === 0 ? undefined : slots[slots.length - 1]!.action;
}

function syncAction() {
  const next = visibleAction();
  if (usePageActionStore.getState().action !== next) usePageActionStore.setState({ action: next });
}

/** 이 id의 본동사를 올리거나 갱신한다. 새로 등록한 쪽이 위에 온다. */
export function registerPageAction(id: number, action: PageAction | null) {
  const index = slots.findIndex((slot) => slot.id === id);
  if (index === -1) slots = [...slots, { id, action }];
  else if (slots[index]!.action === action) return;
  else slots = slots.map((slot, i) => (i === index ? { id, action } : slot));
  syncAction();
}

/** 이 id의 등록을 뺀다. 더 나중에 올라온 등록이 있으면 그것이 남는다. */
export function releasePageAction(id: number) {
  if (!slots.some((slot) => slot.id === id)) return;
  slots = slots.filter((slot) => slot.id !== id);
  syncAction();
}

export function resetPageActionStore() {
  slots = [];
  usePageActionStore.setState({ action: undefined });
}

/**
 * 이 컴포넌트가 마운트된 동안 상단 바 본동사를 정한다.
 * 여러 호출이 겹치면 나중에 마운트된 쪽이 보이고, 빠지면 이전 등록으로 돌아간다.
 */
export function usePageAction(action: PageAction | null) {
  const slotId = useRef(0);
  if (slotId.current === 0) slotId.current = nextSlotId++;

  useLayoutEffect(() => {
    const id = slotId.current;
    return () => releasePageAction(id);
  }, []);

  useLayoutEffect(() => {
    registerPageAction(slotId.current, action);
  });
}
